// src/pages/dashboardScope.ts — which organisation the Overview dashboard reads, by audience.
//
// Before 2026-10-14 the Overview branched on ROLE alone: any provider/master role sent the
// org filter's value, and "all" (the default) sent nothing. So a provider admin who had
// switched to "Acting as Atmantan" still saw the provider's whole tree, and a provider
// whose own org has no business_id hit the backend's "all" path. The rules now:
//
//   * acting as / belonging to a BUSINESS  -> that business, no filter shown
//   * a provider (service org, not root)   -> the picked org, else the provider itself
//                                             (the backend expands an org to its subtree)
//   * the platform (master/platform admin) -> the picked org, else everything (undefined)
import { Organization } from 'types';

const PLATFORM_ROLES = ['master_admin', 'platform_admin'];
const FILTER_ROLES = ['master_admin', 'platform_admin', 'super_admin', 'service_admin', 'service_user'];

export interface DashboardScopeInput {
  currentOrganization: Pick<Organization, 'id' | 'type'> | null | undefined;
  role: string | undefined;
  /** The org filter's value; 'all' or '' when nothing is picked. */
  selectedOrgId: string;
}

/** True when the org filter should be shown at all. */
export function showsOrgFilter({ currentOrganization, role }: DashboardScopeInput): boolean {
  if (currentOrganization?.type === 'business') return false;
  return FILTER_ROLES.includes((role || '').toLowerCase());
}

/** The organizationId to send to /hourly-reports; undefined means "everything I can see". */
export function dashboardOrgId(input: DashboardScopeInput): string | undefined {
  const { currentOrganization, role, selectedOrgId } = input;
  const r = (role || '').toLowerCase();
  if (currentOrganization?.type === 'business') return currentOrganization.id;
  const picked = selectedOrgId && selectedOrgId !== 'all' ? selectedOrgId : undefined;
  if (picked && showsOrgFilter(input)) return picked;
  if (PLATFORM_ROLES.includes(r)) return undefined;
  return currentOrganization?.id;
}

/**
 * The calendar day (YYYY-MM-DD) an hour bucket belongs to, in the VIEWER's zone — IST for
 * every user today. Splitting the UTC timestamp put 00:00–05:30 IST on the previous day.
 */
export function localDayKey(utcHour: string | number | Date): string {
  let d: Date;
  if (typeof utcHour === 'string') {
    const s = utcHour;
    d = new Date(!s.includes('Z') && !s.includes('+') && !s.includes('-', 10) ? s + 'Z' : s);
  } else {
    d = new Date(utcHour);
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
